// Timeline lines that name who acted are one translatable sentence per actor
// form, not a subject rendered next to a separately translated verb phrase.
//
// Spanish conjugates a verb for its subject, so a split cannot be translated:
// the second-person subject "Tú" ends up beside a third-person verb. Giving the
// translation the whole sentence lets it drop the pronoun and conjugate instead
// ("Respondiste por SMS").
//
// Each phrase below expands into three keys — one naming the current agent, one
// naming another agent, one for an unattributed actor — so a translation can
// word each case independently. `<actor>` wraps whatever the translation decides
// the actor is, which is not always a noun: in Spanish it is the conjugated verb.
//
// These keys are built at render time, so i18next-parser cannot see them and
// `pnpm i18n:extract` would prune them. `catalog_dynamic_keys.test.js` walks
// ACTOR_PHRASES and fails if any expansion goes missing from the catalogs.

// Phrase fragments, each completing the sentence that starts with `<actor>`.
// Adding one here means adding all three of its expansions to both catalogs —
// catalog_dynamic_keys.test.js enforces that.
export const ACTOR_PHRASES = Object.freeze({
  ADDED_TOPIC: 'added topic <topics>{{topics}}</topics>',
  ADDED_TOPICS: 'added topics <topics>{{topics}}</topics>',
  CLOSED_CONVERSATION: 'closed this conversation',
  CLOSED_TASK: 'closed a <taskLink>Task</taskLink>',
  CREATED_CONVERSATION_SUMMARY: 'created a conversation summary',
  CREATED_TASK: 'created a task',
  EDITED_TASK: 'edited <taskLink>Task</taskLink>',
  FORWARDED_EMAIL: 'forwarded an email',
  OPENED_CONVERSATION: 'opened this conversation',
  REMOVED_TOPIC: 'removed topic <topics>{{topics}}</topics>',
  REMOVED_TOPICS: 'removed topics <topics>{{topics}}</topics>',
  REOPENED_TASK: 'reopened a <taskLink>Task</taskLink>',
  REPLIED_TO_INSTAGRAM_COMMENT: 'replied to Instagram comment',
  REPLIED_TO_INSTAGRAM_COMMENT_LINKED: 'replied to Instagram <commentLink>comment</commentLink>',
  REPLIED_VIA_CHANNEL: 'replied via {{channel}}',
  SENT_EMAIL: 'sent an email',
});

const CURRENT_AGENT = '<actor>You</actor>';
const NAMED_AGENT = '<actor>{{agentName}}</actor>';
const UNKNOWN_AGENT = '<actor>An agent</actor>';

/**
 * The key for one phrase in the actor form that matches who acted.
 *
 * @param {string} phrase one of ACTOR_PHRASES
 * @param {object} [agent] the acting agent, absent when unattributed
 * @param {string} currentAgentId the reading agent
 * @returns {string} an i18n key
 */
export function actorSentenceKey(phrase, agent, currentAgentId) {
  if (!agent) {
    return `${UNKNOWN_AGENT} ${phrase}`;
  }
  if (currentAgentId === agent.id) {
    return `${CURRENT_AGENT} ${phrase}`;
  }
  return `${NAMED_AGENT} ${phrase}`;
}

/**
 * Every key a phrase expands to. Used by the catalog specs, which cannot reach
 * these through the extractor.
 *
 * @param {string} phrase one of ACTOR_PHRASES
 * @returns {string[]}
 */
export function actorSentenceKeys(phrase) {
  return [CURRENT_AGENT, NAMED_AGENT, UNKNOWN_AGENT].map(actor => `${actor} ${phrase}`);
}

/**
 * Every key every phrase expands to.
 *
 * @returns {string[]}
 */
export function allActorSentenceKeys() {
  return Object.values(ACTOR_PHRASES).flatMap(actorSentenceKeys);
}
